import type { TypePermission } from '@/features/domaine/types/opco/Permissions/TypePermission'
import type { IconName } from '@designSystem/fondation/icons/iconsList'
import type { IndicatorConfig } from '../indicateurTdb/IndicateurTdb.types'
import { API_PATH_DRF_DOSSIERS } from '@/features/interface/recherches/drf/useDRFRechercheDossierStore.ts'
import { API_PATH_DR_DOSSIERS } from '@/features/interface/recherches/dr/useDRRechercheStore.ts'
import { API_PATH_PROFORMA } from '@/features/interface/recherches/proforma/useRechercheProformaStore'

export type IndicateurProps = {
  titre?: DocumentLabel
  count?: number
  loading?: boolean
  slowLoading?: boolean
  error?: boolean
}

export type DocumentCardProps = {
  type: DocumentType
  permissions: TypePermission[]
  indicateurs: Record<VariantType, IndicateurProps>
}

export type DocumentCardByType = Record<DocumentType, DocumentCardProps>

export enum DocumentLabel {
  EnCoursAnalyse = "en cours d'analyse",
  DossiersCompleter = 'dossiers à compléter',
  NouvellesFactures = 'nouvelles factures',
  FacturesRegulariser = 'factures à régulariser',
  DemandeAValider = 'demande(s) à valider',
  DemandeAccepterRefuser = 'demande(s) acceptée(s) ou refusée(s)',
  // Réservations de fonds (DRF)
  DRFACompleter = 'à compléter',
  DRFAccordees = 'accordées',
  // Demandes de remboursement (DR)
  DRATraiter = 'à traiter',
  ProformaAttenteFacture = 'proforma en attente de facture',
}

export const DocumentType = ['dossiers', 'factures', 'remboursements', 'proforma'] as const
export type DocumentType = (typeof DocumentType)[number]

export const VariantType = ['primary', 'secondary'] as const
export type VariantType = (typeof VariantType)[number]

export type DocumentCardEmit = {
  (event: 'redirection-document', variant: VariantType): void
  (event: 'ajout-document'): void
  (event: 'retry-fetch', variant: VariantType): void
}

export interface DocumentTypeConfig {
  typeLabel: string
  iconName: IconName
  buttonLabel: string
  emptyLabel: string
  errorLabel: string
  primary: IndicatorConfig
  secondary: IndicatorConfig
}

export const permissionsMap: Record<DocumentType, TypePermission[]> = {
  dossiers: ['AF', 'CPRO', 'CAPP', 'PROA', 'FPE'],
  factures: ['Facture'],
  remboursements: ['FPE'],
  proforma: ['DR'],
}

export const documentConfig: Record<DocumentType, DocumentTypeConfig> = {
  dossiers: {
    typeLabel: 'Dossiers',
    iconName: 'folder-open',
    buttonLabel: 'Créer un dossier',
    emptyLabel: 'Retrouvez ici vos dossiers incomplets ou en attente de facturation',
    errorLabel: 'Echec réseau (dossiers)',
    primary: {
      route: `/formation/demandes`,
      labelKey: 'DossierIndicateur1',
      getLabel: (tbd) => tbd.DossierIndicateur1.label,
      getPageRequest: (tbd) => tbd.DossierIndicateur1.pageRequest,
    },
    secondary: {
      route: '/dossiers',
      labelKey: 'DossierIndicateur2',
      getLabel: (tbd) => tbd.DossierIndicateur2.label,
      getPageRequest: (tbd) => tbd.DossierIndicateur2.pageRequest,
    },
  },
  factures: {
    typeLabel: 'Factures',
    iconName: 'bill',
    buttonLabel: 'Déposer une facture',
    emptyLabel: 'Retrouvez ici vos nouvelles factures et les factures en relance',
    errorLabel: 'Echec réseau (factures)',
    primary: {
      route: '/factures',
      labelKey: 'FactureIndicateur1',
      getLabel: (tbd) => tbd.FactureIndicateur1.label,
      getPageRequest: (tbd) => tbd.FactureIndicateur1.pageRequest,
    },
    secondary: {
      route: '/factures',
      labelKey: 'FactureIndicateur2',
      getLabel: (tbd) => tbd.FactureIndicateur2.label,
      getPageRequest: (tbd) => tbd.FactureIndicateur2.pageRequest,
    },
  },
  remboursements: {
    typeLabel: 'Réservations de fonds',
    iconName: 'wallet',
    buttonLabel: 'Créer une DRF',
    emptyLabel: 'Retrouvez ici vos réservations de fonds à compléter ou accordées',
    errorLabel: 'Echec réseau (réservations de fonds)',
    primary: {
      route: API_PATH_DRF_DOSSIERS,
      labelKey: 'DRFIndicateur1',
      getLabel: (tbd) => tbd.DRFIndicateur1.label,
      getPageRequest: (tbd) => tbd.DRFIndicateur1.pageRequest,
    },
    secondary: {
      route: API_PATH_DRF_DOSSIERS,
      labelKey: 'DRFIndicateur2',
      getLabel: (tbd) => tbd.DRFIndicateur2.label,
      getPageRequest: (tbd) => tbd.DRFIndicateur2.pageRequest,
    },
  },
  proforma: {
    typeLabel: 'Demandes de remboursement',
    iconName: 'add-square',
    buttonLabel: 'Créer une DR',
    emptyLabel: 'Retrouvez ici vos demandes de remboursement à traiter',
    errorLabel: 'Echec réseau (demandes de remboursement)',
    primary: {
      route: API_PATH_DR_DOSSIERS,
      labelKey: 'DRIndicateur1',
      getLabel: (tbd) => tbd.DRIndicateur1.label,
      getPageRequest: (tbd) => tbd.DRIndicateur1.pageRequest,
    },
    secondary: {
      route: API_PATH_PROFORMA,
      labelKey: 'DRIndicateur2',
      getLabel: (tbd) => tbd.DRIndicateur2.label,
      getPageRequest: (tbd) => tbd.DRIndicateur2.pageRequest,
    },
  },
} as const
